/**
 * Contest page model (docs/contest-ux.md): pure and unit-tested in contestMarkets.test.ts. Turns
 * one contest's FULL liquidity plus its market definitions into the page's category pills and
 * soft market cards.
 *
 *   categories   Main · Spreads · Totals · Player props · Other. Shapes come from
 *                `classifyShape` (via `buildTabs`). Main is the game's winner market (the
 *                moneyline / 3-way / match winner, or every market on a futures contest). Player
 *                props are the markets whose definition says `category: "player_stat"`.
 *                Over/under markets that fan out by participant (team totals) are Totals.
 *                Everything else (period 3-ways, overtime…) is Other.
 *   main line    the docs' rule (/developers/market-model → "Pick a main line"): both sides
 *                fillable, then the most venues, then closest to even, then the smallest line.
 *   player props one card per player, one row per stat, each row on its own main line.
 *
 * Built on the structural shapes in lib/books.ts so real `LiquidityPosition`s drop straight in
 * and come back out untouched (they go to TradeTicket as-is).
 */
import {
  buildTabs,
  groupSpreadLines,
  groupTotalLines,
  sidesOf,
  type BookTab,
  type MarketLike,
  type PartnerLike,
  type PositionLike,
} from "./books.js";
import {
  bestQuote,
  isActionable,
  isGame,
  moneylineKey,
  PERIOD_RE,
  teamsOf,
  type ContestLike,
} from "./marketsBoard.js";

/* ----------------------------------------------------------------- categories */

export type Category = "main" | "spreads" | "totals" | "props" | "other";

export const CATEGORIES: { key: Category; label: string; empty: string }[] = [
  { key: "main", label: "Main", empty: "No main markets for this game yet" },
  { key: "spreads", label: "Spreads", empty: "No spreads for this game yet" },
  { key: "totals", label: "Totals", empty: "No totals for this game yet" },
  { key: "props", label: "Player props", empty: "No player props for this game yet" },
  { key: "other", label: "Other", empty: "No other markets for this game yet" },
];

export const isCategory = (s: string | null | undefined): s is Category =>
  CATEGORIES.some((c) => c.key === s);

export const categoryLabel = (c: Category): string => CATEGORIES.find((x) => x.key === c)?.label ?? c;

/** A player prop: the market definition says so (`category: "player_stat"`), or the key/label does. */
export function isPlayerMarket(key: string, market: MarketLike | undefined): boolean {
  if (market?.category) return market.category === "player_stat";
  return /^player_/.test(key) || /^player\b/i.test(market?.label ?? "");
}

/**
 * Which pill a market lands under. `mainKey` is the game's winner market (null on a futures
 * contest, where every market is an outright and goes to Main).
 */
export function categoryOf(
  tab: Pick<BookTab, "marketKey" | "shape" | "positions">,
  market: MarketLike | undefined,
  ctx: { game: boolean; mainKey: string | null },
): Category {
  if (!ctx.game) return "main";
  if (tab.marketKey === ctx.mainKey) return "main";
  if (isPlayerMarket(tab.marketKey, market)) return "props";
  if (tab.shape === "spread") return "spreads";
  if (tab.shape === "total") return "totals";
  const sides = sidesOf(tab.positions);
  // Over/under fanned out by participant (team totals): a total, one row per team.
  if (tab.shape === "prop" && (sides.has("over") || sides.has("under"))) return "totals";
  // Another head-to-head winner market that isn't a period market.
  if (tab.shape === "moneyline" && sides.has("home") && sides.has("away") && !PERIOD_RE.test(tab.marketKey)) {
    return "main";
  }
  return "other";
}

/* ---------------------------------------------------------------- rungs/sides */

/** One line of a ladder (or the single book of a no-line market) and its paired sides. */
export interface Rung {
  /** Stable key: the normalized line ("-2.5"), or "-" for a market with no line. */
  key: string;
  /** Home-perspective for spreads, raw for totals; null with no line. */
  line: number | null;
  /** One position per side, in display order (away|home, over|under, yes|no). */
  sides: PositionLike[];
}

export type LineKind = "spread" | "total" | "none";

/** Display order within a rung: away before home (the page reads "away @ home"). */
const DISPLAY_SIDE_RANK: Record<string, number> = { away: 0, home: 1, over: 0, under: 1, yes: 0, no: 1 };

const norm = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();
const actionableCount = (p: PositionLike) => (p.partner_liquidities ?? []).filter(isActionable).length;
const actionableSize = (p: PositionLike) =>
  (p.partner_liquidities ?? []).filter(isActionable).reduce((n, pl) => n + (pl.available ?? 0), 0);
const hasActionable = (p: PositionLike) => (p.partner_liquidities ?? []).some(isActionable);

/**
 * One position per side. The feed can carry several positions for the same outcome (distinct
 * market_side_ids, see FRICTION.md); keep the one most venues quote, then the deepest.
 */
export function dedupeSides(positions: PositionLike[]): PositionLike[] {
  const by = new Map<string, PositionLike>();
  for (const p of positions) {
    if (!hasActionable(p)) continue;
    const k = norm(p.side_key) || p.position_hash;
    const cur = by.get(k);
    if (
      !cur ||
      actionableCount(p) > actionableCount(cur) ||
      (actionableCount(p) === actionableCount(cur) && actionableSize(p) > actionableSize(cur))
    ) {
      by.set(k, p);
    }
  }
  return [...by.values()].sort(
    (a, b) => (DISPLAY_SIDE_RANK[norm(a.side_key)] ?? 9) - (DISPLAY_SIDE_RANK[norm(b.side_key)] ?? 9),
  );
}

/** The rungs of a market (or one participant's slice of it), ascending by line. */
export function rungsFor(positions: PositionLike[], kind: LineKind): Rung[] {
  if (kind === "none") {
    const sides = dedupeSides(positions);
    return sides.length ? [{ key: "-", line: null, sides }] : [];
  }
  const groups = kind === "spread" ? groupSpreadLines(positions) : groupTotalLines(positions);
  return groups
    .map((g) => ({ key: g.key, line: g.line, sides: dedupeSides(g.positions) }))
    .filter((r) => r.sides.length > 0);
}

/** Does this slice carry real lines (a non-zero `variable`)? */
const hasLines = (positions: PositionLike[]) =>
  positions.some((p) => p.variable != null && p.variable !== "" && Number(p.variable) !== 0);

/* ------------------------------------------------------------------ main line */

/** Venues with a fillable price on any side of the rung. */
export function rungVenues(r: Rung): number {
  const s = new Set<string>();
  for (const side of r.sides) for (const pl of side.partner_liquidities ?? []) if (isActionable(pl)) s.add(pl.partner_id);
  return s.size;
}

/** Mean distance of each side's best price from 50¢ (Infinity when nothing is priced). */
function evenness(r: Rung): number {
  const ds = r.sides.map((s) => bestQuote(s)?.price).filter((p): p is number => typeof p === "number");
  return ds.length ? ds.reduce((n, p) => n + Math.abs(p - 0.5), 0) / ds.length : Infinity;
}

const twoSided = (r: Rung) => r.sides.length >= 2 && r.sides.every((s) => bestQuote(s) != null);

/**
 * The main line, per the docs: both sides have a fillable price, then the most venues, then
 * closest to even, then the smallest line (|line|, then the lower one). Computed across EVERY
 * venue so the line doesn't jump when Real mode narrows the prices. Returns an index into
 * `rungs` (0 when empty).
 */
export function mainLineIndex(rungs: Rung[]): number {
  let best = 0;
  for (let i = 1; i < rungs.length; i++) if (compareRungs(rungs[i], rungs[best]) < 0) best = i;
  return best;
}

function compareRungs(a: Rung, b: Rung): number {
  return (
    Number(twoSided(b)) - Number(twoSided(a)) ||
    rungVenues(b) - rungVenues(a) ||
    evenness(a) - evenness(b) ||
    Math.abs(a.line ?? 0) - Math.abs(b.line ?? 0) ||
    (a.line ?? 0) - (b.line ?? 0)
  );
}

/** Step through alternate lines: ‹ goes to the next lower line, › to the next higher. Clamped. */
export function stepRung(count: number, index: number, dir: -1 | 1): number {
  if (count <= 0) return 0;
  return Math.min(count - 1, Math.max(0, index + dir));
}

/**
 * The rung to show: the user's pick while that line still exists, else the main line. Derived
 * on render on purpose (a stream push rebuilds the rungs; an effect would reset the pick).
 */
export function resolveRung(rungs: Rung[], selectedKey: string | null, mainIndex: number): number {
  if (selectedKey != null) {
    const i = rungs.findIndex((r) => r.key === selectedKey);
    if (i >= 0) return i;
  }
  return Math.min(mainIndex, Math.max(0, rungs.length - 1));
}

/* -------------------------------------------------------------------- cards */

/** One row of a row card: a player's stat, a team's total, or one outcome of a 3-way/futures. */
export interface Row {
  key: string;
  label: string;
  marketKey: string;
  /** The stat (market_key) a player row belongs to, for the stat filter. */
  stat?: string;
  participantId?: string | null;
  lineKind: LineKind;
  rungs: Rung[];
  mainIndex: number;
}

interface CardBase {
  /** Stable across stream pushes: `m:<market_key>` or `p:<participant_id>`. */
  key: string;
  category: Category;
  title: string;
  marketKeys: string[];
}

export type MarketCard =
  /** A two-way (or one-sided) book with no line: moneyline, match winner, overtime. */
  | (CardBase & { kind: "sides"; sides: PositionLike[] })
  /** A spread/total ladder: the main line plus ‹ › through the alternates. */
  | (CardBase & { kind: "ladder"; lineKind: "spread" | "total"; rungs: Rung[]; mainIndex: number })
  /** One row per participant or stat: 3-way results, futures, team totals, a player's props. */
  | (CardBase & {
      kind: "rows";
      rowKind: "outcomes" | "lines";
      rows: Row[];
      imageUrl?: string | null;
    });

function periodRank(key: string): number {
  if (/^(first_half|1st_half|h1_)/.test(key)) return 1;
  if (/^(second_half|2nd_half|h2_)/.test(key)) return 2;
  const q = /^[qp](\d)_/.exec(key);
  if (q) return 2 + Number(q[1]);
  return 0;
}

const STAT_ORDER = [/points/, /rebounds/, /assists/, /three|3pt/, /steals/, /blocks/];
function statRank(key: string): number {
  const i = STAT_ORDER.findIndex((re) => re.test(key));
  return i < 0 ? STAT_ORDER.length : i;
}

/** "Player Total Points" → "Points"; "Player Three-Pointers Made" → "Three-Pointers Made". */
export function statLabel(label: string | null | undefined, key: string): string {
  const base = (label ?? "").replace(/^player\s+/i, "").replace(/^total\s+/i, "").trim();
  if (base) return base;
  return key
    .replace(/^player_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Best all-venue buy price of a position (0 when none), for ordering favourites. */
const topPrice = (p: PositionLike | undefined) => (p ? ((bestQuote(p)?.price as number | undefined) ?? 0) : 0);

function outcomeRows(
  tab: BookTab,
  contest: ContestLike,
  main: boolean,
): Row[] {
  const byParticipant = new Map<string, PositionLike[]>();
  for (const p of tab.positions) {
    const id = p.participant_id ?? p.position_hash;
    let g = byParticipant.get(id);
    if (!g) byParticipant.set(id, (g = []));
    g.push(p);
  }
  const lineKind: LineKind = hasLines(tab.positions) ? "total" : "none";
  const rows: Row[] = [];
  for (const [id, group] of byParticipant) {
    const rungs = rungsFor(group, lineKind);
    if (!rungs.length) continue;
    rows.push({
      key: id,
      label: group[0].participant_name ?? group[0].title ?? "—",
      marketKey: tab.marketKey,
      participantId: group[0].participant_id ?? null,
      lineKind,
      rungs,
      mainIndex: mainLineIndex(rungs),
    });
  }
  const teams = teamsOf(contest);
  const yesOf = (r: Row) => r.rungs[r.mainIndex]?.sides[0];
  const threeWay = rows.length <= 3 && (main || rows.some((r) => /^(draw|tie)$/i.test(r.label.trim())));
  if (teams && threeWay) {
    // 3-way result: away, draw, home — like the hero and the Markets card.
    const rank = (r: Row) => {
      const n = norm(r.label);
      if (n === norm(teams.away.name) || n === norm(teams.away.abbr)) return 0;
      if (n === "draw" || n === "tie") return 1;
      if (n === norm(teams.home.name) || n === norm(teams.home.abbr)) return 2;
      return 3;
    };
    return rows.sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label));
  }
  // Futures / other multi-outcome: favourites first.
  return rows.sort((a, b) => topPrice(yesOf(b)) - topPrice(yesOf(a)) || a.label.localeCompare(b.label));
}

function cardForTab(tab: BookTab, category: Category, contest: ContestLike, mainKey: string | null): MarketCard | null {
  const base = { key: `m:${tab.marketKey}`, category, title: tab.label, marketKeys: [tab.marketKey] };
  if (tab.shape === "spread" || tab.shape === "total") {
    const rungs = rungsFor(tab.positions, tab.shape);
    return rungs.length ? { ...base, kind: "ladder", lineKind: tab.shape, rungs, mainIndex: mainLineIndex(rungs) } : null;
  }
  if (tab.shape === "moneyline") {
    const sides = dedupeSides(tab.positions);
    return sides.length ? { ...base, kind: "sides", sides } : null;
  }
  const sides = sidesOf(tab.positions);
  const overUnder = sides.has("over") || sides.has("under");
  const rows = overUnder
    ? outcomeRows(tab, contest, false).sort((a, b) => a.label.localeCompare(b.label))
    : outcomeRows(tab, contest, tab.marketKey === mainKey);
  return rows.length ? { ...base, kind: "rows", rowKind: overUnder ? "lines" : "outcomes", rows } : null;
}

export interface StatOption {
  /** market_key, e.g. `player_points`. */
  key: string;
  label: string;
  /** Players with this stat. */
  count: number;
}

export interface ContestModel {
  cards: MarketCard[];
  byCategory: Record<Category, MarketCard[]>;
  /** The stat filter pills for Player props, in display order. */
  stats: StatOption[];
  /** Venues with a fillable price anywhere on the contest. */
  venues: number;
  /** market_keys with no market definition (logged once by the page). */
  unjoinedKeys: string[];
}

export function buildContestModel(
  positions: PositionLike[],
  markets: MarketLike[],
  contest: ContestLike,
): ContestModel {
  const { tabs, unjoinedKeys } = buildTabs(positions, markets);
  const marketByKey = new Map<string, MarketLike>();
  for (const m of markets) if (m.key) marketByKey.set(m.key, m);
  const game = isGame(contest);
  const mainKey = game ? moneylineKey(positions) : null;

  const cards: MarketCard[] = [];
  const players = new Map<string, { name: string; image: string | null; rows: Row[] }>();

  for (const tab of tabs) {
    const market = marketByKey.get(tab.marketKey);
    const category = categoryOf(tab, market, { game, mainKey });
    if (category !== "props") {
      const card = cardForTab(tab, category, contest, mainKey);
      if (card) cards.push(card);
      continue;
    }
    // Player props: split by player, one row per stat.
    const byPlayer = new Map<string, PositionLike[]>();
    for (const p of tab.positions) {
      const id = p.participant_id ?? p.position_hash;
      let g = byPlayer.get(id);
      if (!g) byPlayer.set(id, (g = []));
      g.push(p);
    }
    for (const [id, group] of byPlayer) {
      const lineKind: LineKind = hasLines(group) ? "total" : "none";
      const rungs = rungsFor(group, lineKind);
      if (!rungs.length) continue;
      let pl = players.get(id);
      if (!pl) {
        const named = group.find((p) => p.participant_name) ?? group[0];
        pl = {
          name: named.participant_name ?? named.title ?? id,
          image: (group.find((p) => (p as { participant_image_url?: string | null }).participant_image_url) as
            | { participant_image_url?: string | null }
            | undefined)?.participant_image_url ?? null,
          rows: [],
        };
        players.set(id, pl);
      }
      pl.rows.push({
        key: tab.marketKey,
        label: statLabel(tab.label, tab.marketKey),
        marketKey: tab.marketKey,
        stat: tab.marketKey,
        participantId: id,
        lineKind,
        rungs,
        mainIndex: mainLineIndex(rungs),
      });
    }
  }

  const venuesOfRows = (rows: Row[]) => rows.reduce((n, r) => n + rungVenues(r.rungs[r.mainIndex]), 0);
  const playerCards: MarketCard[] = [...players.entries()]
    .map(([id, pl]) => {
      pl.rows.sort((a, b) => statRank(a.marketKey) - statRank(b.marketKey) || a.label.localeCompare(b.label));
      return {
        key: `p:${id}`,
        category: "props" as const,
        title: pl.name,
        marketKeys: pl.rows.map((r) => r.marketKey),
        kind: "rows" as const,
        rowKind: "lines" as const,
        rows: pl.rows,
        imageUrl: pl.image,
      };
    })
    .sort(
      (a, b) =>
        b.rows.length - a.rows.length || venuesOfRows(b.rows) - venuesOfRows(a.rows) || a.title.localeCompare(b.title),
    );

  const byCategory: Record<Category, MarketCard[]> = { main: [], spreads: [], totals: [], props: [], other: [] };
  for (const c of cards) byCategory[c.category].push(c);
  // The game's own market leads its pill (moneyline, `spread`, `total`), then full-game before
  // halves before quarters, then by label.
  const lead = (c: MarketCard) => c.key === `m:${mainKey}` || c.key === "m:spread" || c.key === "m:total";
  const order = (a: MarketCard, b: MarketCard) =>
    Number(lead(b)) - Number(lead(a)) ||
    periodRank(a.marketKeys[0]) - periodRank(b.marketKeys[0]) ||
    a.title.localeCompare(b.title);
  for (const k of ["main", "spreads", "totals", "other"] as const) byCategory[k].sort(order);
  byCategory.props = playerCards;

  const statCount = new Map<string, StatOption>();
  for (const c of playerCards) {
    if (c.kind !== "rows") continue;
    for (const r of c.rows) {
      const cur = statCount.get(r.marketKey);
      if (cur) cur.count++;
      else statCount.set(r.marketKey, { key: r.marketKey, label: r.label, count: 1 });
    }
  }
  const stats = [...statCount.values()].sort(
    (a, b) => statRank(a.key) - statRank(b.key) || b.count - a.count || a.label.localeCompare(b.label),
  );

  const venues = new Set<string>();
  for (const p of positions) for (const pl of p.partner_liquidities ?? []) if (isActionable(pl)) venues.add(pl.partner_id);

  const all = CATEGORIES.flatMap((c) => byCategory[c.key]);
  return { cards: all, byCategory, stats, venues: venues.size, unjoinedKeys };
}

/** The pill to open on: the first category with markets. */
export function defaultCategory(byCategory: Record<Category, MarketCard[]>): Category {
  return CATEGORIES.find((c) => byCategory[c.key].length > 0)?.key ?? "main";
}

/** A player card narrowed to one stat (null when the player doesn't have it). */
export function withStat(card: MarketCard, stat: string | null): MarketCard | null {
  if (!stat || card.kind !== "rows" || card.category !== "props") return card;
  const rows = card.rows.filter((r) => r.stat === stat);
  return rows.length ? { ...card, rows } : null;
}

/* ------------------------------------------------------------------ search */

function sideNames(card: MarketCard): string[] {
  const out: string[] = [];
  const add = (p: PositionLike) => out.push(p.participant_name ?? "", p.title ?? "");
  if (card.kind === "sides") card.sides.forEach(add);
  if (card.kind === "ladder") card.rungs[card.mainIndex]?.sides.forEach(add);
  return out;
}

/**
 * Cards matching a search across every category. A card whose title (market, player) matches
 * stays whole; otherwise a row card keeps only its matching rows ("points" → each player's
 * Points row).
 */
export function searchCards(cards: MarketCard[], query: string): MarketCard[] {
  const q = query.trim().toLowerCase();
  if (!q) return cards;
  const has = (s: string | null | undefined) => (s ?? "").toLowerCase().includes(q);
  const out: MarketCard[] = [];
  for (const c of cards) {
    if (has(c.title) || has(categoryLabel(c.category)) || sideNames(c).some(has)) {
      out.push(c);
      continue;
    }
    if (c.kind === "rows") {
      const rows = c.rows.filter((r) => has(r.label) || has(r.marketKey.replace(/_/g, " ")));
      if (rows.length) out.push({ ...c, rows });
    }
  }
  return out;
}

/* --------------------------------------------------------------- deep links */

export interface DeepTarget {
  category: Category;
  cardKey: string;
  rowKey?: string;
  rungKey?: string;
}

/**
 * Where `?market=<market_key>` and/or a focused position (`?pos=<position_hash>`, the signals
 * chip, the best edge) land: the card (and row / line) that carries it. The position wins when
 * both are given; null when neither is on the page.
 */
export function findTarget(
  cards: MarketCard[],
  link: { market?: string | null; pos?: string | null },
): DeepTarget | null {
  if (link.pos) {
    for (const c of cards) {
      const hit = (rungs: Rung[]) => rungs.find((r) => r.sides.some((s) => s.position_hash === link.pos));
      if (c.kind === "sides" && c.sides.some((s) => s.position_hash === link.pos)) {
        return { category: c.category, cardKey: c.key };
      }
      if (c.kind === "ladder") {
        const r = hit(c.rungs);
        if (r) return { category: c.category, cardKey: c.key, rungKey: r.key };
      }
      if (c.kind === "rows") {
        for (const row of c.rows) {
          const r = hit(row.rungs);
          if (r) return { category: c.category, cardKey: c.key, rowKey: row.key, rungKey: r.key };
        }
      }
    }
  }
  if (link.market) {
    const c = cards.find((x) => x.marketKeys.includes(link.market!));
    if (c) {
      const row = c.kind === "rows" && c.category === "props" ? c.rows.find((r) => r.marketKey === link.market) : undefined;
      return { category: c.category, cardKey: c.key, rowKey: row?.key };
    }
  }
  return null;
}

/* ------------------------------------------------------------- venue list */

export interface VenueQuote<P extends PartnerLike = PartnerLike> {
  pl: P;
  /** Can be traded in Real (a connected venue), or always true in Paper. */
  tradeable: boolean;
}

/**
 * Every venue's fillable price for one side, best first: lowest price, then the deepest, then
 * by name. `realVenues` marks which are tradeable in Real (null = Paper: all of them).
 */
export function venueQuotes<P extends PartnerLike>(
  pos: { partner_liquidities?: P[] },
  realVenues: ReadonlySet<string> | null,
): VenueQuote<P>[] {
  return (pos.partner_liquidities ?? [])
    .filter(isActionable)
    .sort(
      (a, b) =>
        (a.price as number) - (b.price as number) ||
        (b.available ?? 0) - (a.available ?? 0) ||
        (a.partner_name ?? a.partner_id).localeCompare(b.partner_name ?? b.partner_id),
    )
    .map((pl) => ({ pl, tradeable: realVenues ? realVenues.has(pl.partner_id) : true }));
}

/** The positions a card's Book / Chart toggle shows: the visible line's sides (or one row's). */
export function sliceOf(card: MarketCard, rungIndex: number, rowKey: string | null, rowRung: (r: Row) => number): PositionLike[] {
  if (card.kind === "sides") return card.sides;
  if (card.kind === "ladder") return card.rungs[rungIndex]?.sides ?? [];
  // A 3-way result: the three "yes" sides together.
  if (card.rowKind === "outcomes" && card.rows.length <= 3 && card.rows.length > 1) {
    return card.rows.map((r) => r.rungs[rowRung(r)]?.sides[0]).filter((p): p is PositionLike => !!p);
  }
  const row = card.rows.find((r) => r.key === rowKey) ?? card.rows[0];
  return row ? (row.rungs[rowRung(row)]?.sides ?? []) : [];
}

/* ------------------------------------------------- game lines (Revision 1) */

/** A row of the Game lines table. */
export type GameSide = "away" | "draw" | "home";

/** The Spread or Total column of the Game lines table: one ladder, stepped as a whole. */
export interface LineColumn {
  cardKey: string;
  marketKey: string;
  title: string;
  kind: "spread" | "total";
  rungs: Rung[];
  mainIndex: number;
}

export interface GameLinesRow {
  side: GameSide;
  label: string;
  /** This row's winner-market position (the moneyline side, or the 3-way "yes"); null if unpriced. */
  moneyline: PositionLike | null;
}

/**
 * Main for a head-to-head game (docs/contest-ux.md, Revision 1): one table, a row per team (plus
 * Draw for a 3-way result) and the columns Moneyline · Spread · Total.
 */
export interface GameLines {
  moneyline: { cardKey: string; marketKey: string; title: string };
  rows: GameLinesRow[];
  spread: LineColumn | null;
  total: LineColumn | null;
  /** The cards the table stands in for on Main (the winner market only; spread/total keep their pills). */
  cardKeys: string[];
}

const ladderCol = (c: MarketCard | undefined): LineColumn | null =>
  c && c.kind === "ladder"
    ? { cardKey: c.key, marketKey: c.marketKeys[0], title: c.title, kind: c.lineKind, rungs: c.rungs, mainIndex: c.mainIndex }
    : null;

/** The full-game ladder of a pill: `spread` / `total` by key, else the first non-period ladder. */
function fullGameLadder(cards: MarketCard[], key: string): LineColumn | null {
  const ladders = cards.filter((c) => c.kind === "ladder");
  return (
    ladderCol(ladders.find((c) => c.key === `m:${key}`)) ??
    ladderCol(ladders.find((c) => !PERIOD_RE.test(c.marketKeys[0]) && !/team/.test(c.marketKeys[0])))
  );
}

/**
 * The Game lines table for a head-to-head game, or null (futures, no teams, or a winner market
 * that doesn't read as away / (draw) / home — those keep their plain cards).
 */
export function gameLines(model: ContestModel, contest: ContestLike): GameLines | null {
  if (!isGame(contest)) return null;
  const teams = teamsOf(contest);
  const card = model.byCategory.main[0];
  if (!teams || !card) return null;
  const name = (t: { name?: string; abbr?: string }) => t.name ?? t.abbr ?? "—";
  let rows: GameLinesRow[];
  if (card.kind === "sides") {
    const of = (s: GameSide) => card.sides.find((p) => norm(p.side_key) === s) ?? null;
    if (!of("away") && !of("home")) return null;
    rows = [
      { side: "away", label: name(teams.away), moneyline: of("away") },
      { side: "home", label: name(teams.home), moneyline: of("home") },
    ];
  } else if (card.kind === "rows" && card.rowKind === "outcomes" && card.rows.length <= 3) {
    const is = (r: Row, t: { name?: string; abbr?: string }) => {
      const n = norm(r.label);
      return n === norm(t.name) || n === norm(t.abbr);
    };
    const yes = (r: Row | undefined) => (r ? (r.rungs[r.mainIndex]?.sides[0] ?? null) : null);
    const away = card.rows.find((r) => is(r, teams.away));
    const home = card.rows.find((r) => is(r, teams.home));
    const draw = card.rows.find((r) => /^(draw|tie)$/i.test(r.label.trim()));
    if (!away || !home) return null;
    rows = [
      { side: "away", label: name(teams.away), moneyline: yes(away) },
      ...(draw ? [{ side: "draw" as const, label: draw.label, moneyline: yes(draw) }] : []),
      { side: "home", label: name(teams.home), moneyline: yes(home) },
    ];
  } else {
    return null;
  }
  return {
    moneyline: { cardKey: card.key, marketKey: card.marketKeys[0], title: card.title },
    rows,
    spread: fullGameLadder(model.byCategory.spreads, "spread"),
    total: fullGameLadder(model.byCategory.totals, "total"),
    cardKeys: [card.key],
  };
}

/**
 * One cell of a Spread / Total column at the shown line. Spread: the row's own side (away / home).
 * Total: Over on the away row, Under on the home row (the sportsbook layout). Draw has none.
 */
export function lineCell(col: LineColumn, rungIndex: number, side: GameSide): PositionLike | null {
  const rung = col.rungs[rungIndex];
  if (!rung || side === "draw") return null;
  const want = col.kind === "spread" ? side : side === "away" ? "over" : "under";
  return rung.sides.find((p) => norm(p.side_key) === want) ?? null;
}

/** The winner-market slice in row order (the insight column's default market). */
export function moneylineSlice(gl: GameLines): PositionLike[] {
  return gl.rows.map((r) => r.moneyline).filter((p): p is PositionLike => !!p);
}

/* ---------------------------------------------------------- popular props */

export interface PopularProp {
  cardKey: string;
  player: string;
  imageUrl?: string | null;
  /** The player's Points row (or their first stat). */
  row: Row;
  /** Venues quoting that row's main line. */
  venues: number;
}

/**
 * Popular props for Main: up to `limit` players, each on their Points line (or first stat), the
 * players whose line the most venues quote first (then the deepest, then by name).
 */
export function popularProps(model: ContestModel, limit = 6): PopularProp[] {
  const out: (PopularProp & { size: number })[] = [];
  for (const c of model.byCategory.props) {
    if (c.kind !== "rows" || c.rows.length === 0) continue;
    const row = c.rows.find((r) => r.marketKey === "player_points" || /^points$/i.test(r.label.trim())) ?? c.rows[0];
    const rung = row.rungs[row.mainIndex];
    if (!rung) continue;
    out.push({
      cardKey: c.key,
      player: c.title,
      imageUrl: c.imageUrl,
      row,
      venues: rungVenues(rung),
      size: rung.sides.reduce((n, s) => n + actionableSize(s), 0),
    });
  }
  return out
    .sort((a, b) => b.venues - a.venues || b.size - a.size || a.player.localeCompare(b.player))
    .slice(0, limit)
    .map(({ size: _size, ...p }) => p);
}

/* ------------------------------------------------------ insight venue matrix */

export interface VenueMatrixRow<P extends PartnerLike = PartnerLike> {
  partnerId: string;
  name: string;
  /** This venue's fillable quote per side (same order as the sides), or null. */
  cells: (P | null)[];
}

/**
 * Every venue's price for every side of a market (the insight column): one row per venue that
 * quotes any side, sorted by the focused side's price (best first; venues without it last), then
 * by how many sides they quote, then by name. `best[i]` is side i's best (lowest) price.
 */
export function venueMatrix<P extends PartnerLike>(
  sides: { partner_liquidities?: P[] }[],
  focus = 0,
): { rows: VenueMatrixRow<P>[]; best: (number | null)[] } {
  const by = new Map<string, VenueMatrixRow<P>>();
  sides.forEach((s, i) => {
    for (const pl of s.partner_liquidities ?? []) {
      if (!isActionable(pl)) continue;
      let r = by.get(pl.partner_id);
      if (!r) by.set(pl.partner_id, (r = { partnerId: pl.partner_id, name: pl.partner_name ?? pl.partner_id, cells: sides.map(() => null) }));
      const cur = r.cells[i];
      if (!cur || (pl.price as number) < (cur.price as number)) r.cells[i] = pl;
    }
  });
  const f = Math.min(Math.max(0, focus), Math.max(0, sides.length - 1));
  const price = (r: VenueMatrixRow<P>) => (r.cells[f]?.price as number | undefined) ?? Infinity;
  const quoted = (r: VenueMatrixRow<P>) => r.cells.filter(Boolean).length;
  const rows = [...by.values()].sort(
    (a, b) => price(a) - price(b) || quoted(b) - quoted(a) || a.name.localeCompare(b.name),
  );
  const best = sides.map((_, i) => {
    const ps = rows.map((r) => r.cells[i]?.price).filter((p): p is number => typeof p === "number");
    return ps.length ? Math.min(...ps) : null;
  });
  return { rows, best };
}
